import React, { useEffect, useState } from 'react';
import { useSearchParams, useNavigate, Link } from 'react-router-dom';
import {
  Container,
  Paper,
  Typography,
  Button,
  Box,
  Card,
  CardContent,
  Alert,
  Chip
} from '@mui/material';
import {
  CheckCircle as SuccessIcon,
  Logout as LogoutIcon,
  Lock as LockIcon,
  Home as HomeIcon
} from '@mui/icons-material';
import MetaTags from '../components/MetaTags';

interface ThankYouProps {
  type?: 'logout' | 'password-reset';
  companySlug?: string;
  companyName?: string;
}

const ThankYou: React.FC<ThankYouProps> = ({ type = 'logout', companySlug, companyName }) => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [detectedCompany, setDetectedCompany] = useState<string | null>(null);
  const [detectedCompanyName, setDetectedCompanyName] = useState<string | null>(null);
  
  // Get type from URL parameters
  const urlType = searchParams.get('type') as 'logout' | 'password-reset' | null;
  const finalType = urlType || type;

  // Detect company context from URL or props
  useEffect(() => {
    if (companySlug && companyName) {
      setDetectedCompany(companySlug);
      setDetectedCompanyName(companyName);
    } else {
      // Try to detect from current URL
      const pathParts = window.location.pathname.split('/');
      if (pathParts.length >= 2 && pathParts[1] && 
          !['admin', 'client', 'account-deactivated', 'admin-signup', 'client-signup', 'reset-password', 'thank-you'].includes(pathParts[1])) {
        setDetectedCompany(pathParts[1]);
        // Convert slug to readable name (e.g., "the-impression" -> "The Impression")
        const readableName = pathParts[1]
          .split('-')
          .map(word => word.charAt(0).toUpperCase() + word.slice(1))
          .join(' ');
        setDetectedCompanyName(readableName);
      }
    }
  }, [companySlug, companyName]);

  const getPageContent = (pageType: 'logout' | 'password-reset') => {
    if (pageType === 'password-reset') {
      return {
        title: 'Password Reset Successful!',
        subtitle: 'Your password has been updated successfully.',
        message: 'You can now log in to your account using your new password.',
        icon: <LockIcon sx={{ fontSize: 64, color: 'success.main' }} />,
        backgroundColor: 'linear-gradient(135deg, #00b894 0%, #00cec9 100%)',
        buttonText: 'Login to Your Account',
        buttonAction: () => {
          if (detectedCompany) {
            navigate(`/${detectedCompany}/login`);
          } else {
            navigate('/admin/login');
          }
        }
      };
    }

    // Default logout content
    return {
      title: 'Thank You for Using LeadForge!',
      subtitle: 'You have been successfully logged out.',
      message: 'Please use the appropriate login link for your account when you want to access LeadForge again.',
      icon: <LogoutIcon sx={{ fontSize: 64, color: 'primary.main' }} />,
      backgroundColor: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
      buttonText: 'Login Again',
      buttonAction: () => {
        if (detectedCompany) {
          navigate(`/${detectedCompany}/login`);
        } else {
          navigate('/admin/login');
        }
      }
    };
  };

  const content = getPageContent(finalType);

  return (
    <>
      <MetaTags 
        title={`${content.title} - LeadForge`}
        description={content.message}
      />
      <Container maxWidth="md" sx={{ py: 8 }}>
        <Paper
          elevation={3}
          sx={{
            p: 6,
            textAlign: 'center',
            background: content.backgroundColor,
            color: 'white',
            mb: 4
          }}
        >
          {content.icon}
          <Typography variant="h3" gutterBottom fontWeight="bold" sx={{ mt: 2 }}>
            {content.title}
          </Typography>
          <Typography variant="h6" sx={{ opacity: 0.9, mb: 2 }}>
            {content.subtitle}
          </Typography>
          <Typography variant="body1" sx={{ opacity: 0.8 }}>
            {content.message}
          </Typography>
        </Paper>

        <Card sx={{ mb: 4 }}>
          <CardContent sx={{ p: 4 }}>
            <Typography variant="h5" gutterBottom sx={{ mb: 3 }}>
              🔐 Account Access Information
            </Typography>

            {detectedCompany && detectedCompanyName ? (
              <>
                <Alert severity="info" sx={{ mb: 3 }}>
                  <Typography variant="body1">
                    <strong>Company Account Detected:</strong> {detectedCompanyName}
                  </Typography>
                </Alert>

                <Box sx={{ mb: 3 }}>
                  <Typography variant="h6" gutterBottom>
                    📍 Your Company Login Link:
                  </Typography>
                  <Chip
                    label={`/${detectedCompany}/login`}
                    color="primary"
                    variant="outlined"
                    sx={{ fontSize: '1rem', p: 1 }}
                  />
                  <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                    Use this link to access your company-specific LeadForge account
                  </Typography>
                </Box>

                <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center', flexWrap: 'wrap' }}>
                  <Button
                    variant="contained"
                    size="large"
                    onClick={content.buttonAction}
                    startIcon={<LockIcon />}
                    sx={{ minWidth: 200 }}
                  >
                    {content.buttonText}
                  </Button>
                  
                  <Button
                    component={Link}
                    to={`/${detectedCompany}/login`}
                    variant="outlined"
                    size="large"
                    startIcon={<HomeIcon />}
                    sx={{ minWidth: 200 }}
                  >
                    Go to Company Login
                  </Button>
                </Box>
              </>
            ) : (
              <>
                <Alert severity="warning" sx={{ mb: 3 }}>
                  <Typography variant="body1">
                    <strong>No Company Context Detected</strong>
                  </Typography>
                  <Typography variant="body2">
                    Please use the appropriate login method for your account type.
                  </Typography>
                </Alert>

                <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center', flexWrap: 'wrap' }}>
                  <Button
                    component={Link}
                    to="/admin/login"
                    variant="contained"
                    size="large"
                    startIcon={<LockIcon />}
                    sx={{ minWidth: 200 }}
                  >
                    Admin Login
                  </Button>
                  
                  <Button
                    component={Link}
                    to="/client-signup"
                    variant="outlined"
                    size="large"
                    startIcon={<HomeIcon />}
                    sx={{ minWidth: 200 }}
                  >
                    Client Signup
                  </Button>
                </Box>
              </>
            )}
          </CardContent>
        </Card>

        <Card>
          <CardContent sx={{ p: 4 }}>
            <Typography variant="h6" gutterBottom sx={{ mb: 2 }}>
              📋 Quick Reference
            </Typography>
            
            <Box sx={{ display: 'grid', gap: 2, gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' } }}>
              <Box>
                <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
                  🏢 Company Users:
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Use your company-specific login link:<br />
                  <code>app.leadforge.theimpression.me/[your-company]/login</code>
                </Typography>
              </Box>
              
              <Box>
                <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
                  👨‍💼 Admin Users:
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Use the admin login:<br />
                  <code>app.leadforge.theimpression.me/admin/login</code>
                </Typography>
              </Box>
            </Box>
          </CardContent>
        </Card>
      </Container>
    </>
  );
};

export default ThankYou;